import type { ExamType } from '@/types/exam';

/**
 * Which of the three groups a family screen files a paper under.
 *
 * Decided by the exam type the office chose when setting the paper and nothing
 * else. The marks report used to add guesses from the exam's name on top —
 * "Monthly Test" landing in Unit Tests by type and in Term Exams by a word in
 * its name — so one exam sat in two tabs and disagreed with the profile's
 * academic tab, which went by type alone. One map, read by both.
 */
export type ExamCategory = 'termExams' | 'unitTests' | 'assignments';

export const EXAM_CATEGORY_BY_TYPE: Record<ExamType, ExamCategory> = {
  MID_TERM: 'termExams',
  FINAL: 'termExams',
  UNIT_TEST: 'unitTests',
  ASSIGNMENT: 'assignments',
  // Marked at the bench or spoken aloud. Grouped with the assignments because
  // they are continuous assessment, not a written paper the term turns on.
  PRACTICAL: 'assignments',
  ORAL: 'assignments',
  // CBSE's names for the same two things: the term-end paper and the tests
  // through the term.
  SUMMATIVE: 'termExams',
  FORMATIVE: 'unitTests',
};

/**
 * The group a paper belongs to, or null for a type this build does not know —
 * the marks response types `examType` as a plain string, and a type added on
 * the server before the client is not to be filed under a guess.
 */
export function examCategoryOf(examType: string): ExamCategory | null {
  return isKnownExamType(examType) ? EXAM_CATEGORY_BY_TYPE[examType] : null;
}

// An own-property check, not `in`: "toString" is in every object.
function isKnownExamType(value: string): value is ExamType {
  return Object.prototype.hasOwnProperty.call(EXAM_CATEGORY_BY_TYPE, value);
}
